import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ColumnDef } from '@tanstack/react-table';
import { Inbox, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Textarea } from '../../components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { DataTable } from '../../components/shared/DataTable';
import { useAuth } from '../../core/contexts/AuthContext';
import { useClientRequests } from '../../core/hooks/useClientRequests';
import { useToast } from '../../core/hooks/useToast';
import { useClients } from '../../core/hooks/useClients';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import type { ClientRequest } from '../../core/models/ClientRequest';
import { UserRole } from '../../core/models/User';

type ReviewMode = 'approve' | 'reject';

const TYPE_LABEL: Record<ClientRequest['type'], string> = {
  feature: 'Feature',
  problem: 'Problem',
};

const STATUS_LABEL: Record<ClientRequest['status'], string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

const intakeEntries = (
  intake: ClientRequest['intake']
): Array<[string, string]> => {
  if (!intake) return [];
  return Object.entries(intake).filter(([, value]) => value.trim().length > 0);
};

/** Compact channel badge for connector-originated submissions. */
const ChatGptSourceBadge: React.FC = () => (
  <Badge variant="outline" className="shrink-0 font-medium">
    via ChatGPT
  </Badge>
);

/**
 * What the client has said about a request: which platform it is for, whether
 * one of their admins has signed it off, and where they would rank it.
 */
const ClientSignals: React.FC<{ request: ClientRequest }> = ({ request }) => {
  const signedOff = request.clientEndorsement;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {request.platform ? (
        <Badge variant="outline">{request.platform}</Badge>
      ) : null}
      {request.status === 'pending' && request.source === 'chatgpt-connector' ? (
        signedOff ? (
          <Badge variant="success" title={signedOff.note || undefined}>
            Signed off{signedOff.byName ? ` by ${signedOff.byName}` : ''}
          </Badge>
        ) : (
          <Badge variant="warning">Awaiting client sign-off</Badge>
        )
      ) : null}
      {request.clientPriority ? (
        <Badge variant="outline" title={request.clientPriority.note || undefined}>
          Client rank #{request.clientPriority.rank}
        </Badge>
      ) : null}
      {request.createdByName ? (
        <span className="text-xs text-gray-500">from {request.createdByName}</span>
      ) : null}
    </div>
  );
};

/** Expandable structured answers from the ChatGPT connector intake flow. */
const IntakeDetails: React.FC<{ intake: Record<string, string> }> = ({
  intake,
}) => {
  const entries = intakeEntries(intake);
  if (entries.length === 0) return null;

  return (
    <details className="mt-2 rounded-md border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-gray-700">
        Intake details ({entries.length})
      </summary>
      <dl className="space-y-2 border-t border-gray-100 px-3 py-2">
        {entries.map(([key, value]) => (
          <div key={key}>
            <dt className="text-xs font-medium text-gray-500">{key}</dt>
            <dd className="whitespace-pre-wrap text-sm text-gray-800">{value}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
};

export const ClientRequestsPage: React.FC = () => {
  const { currentUser, clientId } = useAuth();
  const toast = useToast();
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const { requests, loading, error, approveRequest, rejectRequest } =
    useClientRequests(isSuperAdmin ? null : clientId);
  const { clients: projects } = useClients(isSuperAdmin ? null : clientId);
  const { organizations } = useOrganizations();

  const [statusFilter, setStatusFilter] = useState<'pending' | 'all'>('pending');
  const [reviewing, setReviewing] = useState<ClientRequest | null>(null);
  const [reviewMode, setReviewMode] = useState<ReviewMode>('approve');
  const [reviewNote, setReviewNote] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const projectFieldId = React.useId();

  const organizationNameById = useMemo(() => {
    return organizations.reduce((acc, org) => {
      acc[org.id] = org.name;
      if (org.legacyOrganizationId) {
        acc[org.legacyOrganizationId] = org.name;
      }
      return acc;
    }, {} as Record<string, string>);
  }, [organizations]);

  const projectNameById = useMemo(() => {
    return projects.reduce((acc, project) => {
      acc[project.id] = project.name || project.id;
      return acc;
    }, {} as Record<string, string>);
  }, [projects]);

  const filteredRequests = useMemo(() => {
    if (statusFilter === 'all') return requests;
    return requests.filter((request) => request.status === 'pending');
  }, [requests, statusFilter]);

  const projectsForRequest = useMemo(() => {
    if (!reviewing) return [];
    return projects.filter((project) => project.clientId === reviewing.clientId);
  }, [projects, reviewing]);

  const openReview = (request: ClientRequest, mode: ReviewMode) => {
    setReviewing(request);
    setReviewMode(mode);
    setReviewNote('');
    setActionError(null);
    setSelectedProjectId(request.projectId || '');
  };

  const closeReview = () => {
    if (submitting) return;
    setReviewing(null);
    setActionError(null);
  };

  const handleSubmitReview = async () => {
    if (!reviewing) return;
    setSubmitting(true);
    setActionError(null);
    try {
      if (reviewMode === 'approve') {
        await approveRequest(reviewing, {
          projectId: selectedProjectId || reviewing.projectId,
          reviewNote,
        });
        toast.success(
          'Request approved',
          'A task has been created on the project board.'
        );
      } else {
        await rejectRequest(reviewing.id, reviewNote);
        toast.success('Request rejected', 'The client can see your note.');
      }
      setReviewing(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Review failed');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnDef<ClientRequest>[] = [
    {
      accessorKey: 'title',
      header: 'Request',
      cell: ({ row }) => {
        const request = row.original;
        const headline =
          request.title?.trim() ||
          request.description.trim().slice(0, 80) ||
          'Untitled request';
        const fromChatGpt = request.source === 'chatgpt-connector';
        return (
          <div className="min-w-[220px]">
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-medium text-gray-900">{headline}</div>
              {fromChatGpt ? <ChatGptSourceBadge /> : null}
            </div>
            <ClientSignals request={request} />
            {request.title?.trim() && (
              <div className="mt-1 line-clamp-2 text-sm text-gray-500">
                {request.description}
              </div>
            )}
            {fromChatGpt && request.intake ? (
              <IntakeDetails intake={request.intake} />
            ) : null}
          </div>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant={row.original.type === 'problem' ? 'warning' : 'default'}>
          {row.original.type}
        </Badge>
      ),
    },
    {
      accessorKey: 'clientId',
      header: 'Client',
      cell: ({ row }) =>
        organizationNameById[row.original.clientId] || row.original.clientId,
    },
    {
      accessorKey: 'projectId',
      header: 'Project',
      cell: ({ row }) =>
        row.original.projectId
          ? projectNameById[row.original.projectId] || row.original.projectId
          : '—',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        const variant =
          status === 'approved'
            ? 'success'
            : status === 'rejected'
              ? 'error'
              : 'warning';
        return <Badge variant={variant}>{STATUS_LABEL[status]}</Badge>;
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Submitted',
      cell: ({ row }) => format(row.original.createdAt, 'PP'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const request = row.original;
        if (request.status !== 'pending') {
          return request.taskId ? (
            <span className="text-xs text-gray-500">Task linked</span>
          ) : null;
        }
        if (!isSuperAdmin) return null;
        return (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => openReview(request, 'reject')}
            >
              <X className="mr-1 h-4 w-4" />
              Reject
            </Button>
            <Button size="sm" onClick={() => openReview(request, 'approve')}>
              <Check className="mr-1 h-4 w-4" />
              Approve
            </Button>
          </div>
        );
      },
    },
  ];

  const header = (
    <PageHeader
      title="Client Requests"
      description="Review portal submissions. Approving creates a task on the client board."
      icon={Inbox}
      actions={
        !loading && !error ? (
          <Select
            value={statusFilter}
            onValueChange={(value) =>
              setStatusFilter(value === 'all' ? 'all' : 'pending')
            }
          >
            <SelectTrigger className="w-[180px]" aria-label="Filter by status">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending only</SelectItem>
              <SelectItem value="all">All requests</SelectItem>
            </SelectContent>
          </Select>
        ) : undefined
      }
    />
  );

  if (loading) {
    return (
      <div className="space-y-6">
        {header}
        <LoadingState message="Loading client requests…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {header}
        <ErrorState
          title="Couldn't load client requests"
          message={
            error.message ||
            'The request list could not be reached. Check your connection and try again.'
          }
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}

      {filteredRequests.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title={
            statusFilter === 'pending'
              ? 'No pending requests'
              : 'No client requests yet'
          }
          description={
            statusFilter === 'pending'
              ? 'New feature and problem requests from the client portal will appear here.'
              : 'Clients submit requests from the portal after signing in.'
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={filteredRequests}
          label="Client requests"
          emptyMessage="No requests match this filter."
        />
      )}

      <Dialog open={!!reviewing} onOpenChange={(open) => !open && closeReview()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewMode === 'approve' ? 'Approve request' : 'Reject request'}
            </DialogTitle>
            <DialogDescription>
              {reviewMode === 'approve'
                ? 'Creates a task on the selected project board and notifies the client.'
                : 'Closes the request. Your note is visible to the client.'}
            </DialogDescription>
          </DialogHeader>

          {reviewing && (
            <div className="space-y-4">
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="text-sm font-medium text-gray-900">
                    {reviewing.title?.trim() || 'Untitled request'}
                  </div>
                  {reviewing.source === 'chatgpt-connector' ? (
                    <ChatGptSourceBadge />
                  ) : null}
                </div>
                <ClientSignals request={reviewing} />
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-600">
                  {reviewing.description}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Badge
                    variant={
                      reviewing.type === 'problem' ? 'warning' : 'default'
                    }
                  >
                    {TYPE_LABEL[reviewing.type]}
                  </Badge>
                  <span className="text-xs text-gray-500">
                    {organizationNameById[reviewing.clientId] ||
                      reviewing.clientId}
                  </span>
                </div>
                {reviewing.source === 'chatgpt-connector' &&
                reviewing.intake ? (
                  <IntakeDetails intake={reviewing.intake} />
                ) : null}
              </div>

              {reviewMode === 'approve' && (
                <div className="space-y-1">
                  <span
                    id={projectFieldId}
                    className="block text-sm font-medium text-gray-700"
                  >
                    Project{' '}
                    <span className="text-error" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <Select
                    value={selectedProjectId || undefined}
                    onValueChange={setSelectedProjectId}
                  >
                    <SelectTrigger
                      aria-labelledby={projectFieldId}
                      disabled={projectsForRequest.length === 0}
                    >
                      <SelectValue placeholder="Select project for the task" />
                    </SelectTrigger>
                    <SelectContent>
                      {projectsForRequest.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name || project.id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {projectsForRequest.length === 0 && (
                    <p className="text-xs text-error" role="alert">
                      This client has no projects yet. Create one before
                      approving this request.
                    </p>
                  )}
                </div>
              )}

              <Textarea
                label={
                  reviewMode === 'reject'
                    ? 'Rejection note (optional)'
                    : 'Review note (optional)'
                }
                value={reviewNote}
                onChange={(event) => setReviewNote(event.target.value)}
                rows={3}
                placeholder={
                  reviewMode === 'reject'
                    ? 'Share why this request was rejected'
                    : 'Optional note for the client'
                }
              />

              {actionError && (
                <p className="text-sm text-error" role="alert">
                  {actionError}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeReview}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant={reviewMode === 'reject' ? 'destructive' : 'default'}
              onClick={handleSubmitReview}
              loading={submitting}
              disabled={
                submitting ||
                (reviewMode === 'approve' &&
                  !(selectedProjectId || reviewing?.projectId))
              }
            >
              {reviewMode === 'approve'
                ? 'Approve & create task'
                : 'Reject request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
